import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, Check, RefreshCw, AlertCircle, Clock, Package } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  const checkPendingPOs = async () => {
    setChecking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/notifications/check-pending-pos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.notifications_created > 0) {
        alert(`${response.data.notifications_created} new notifications created!`);
        fetchNotifications();
      } else {
        alert('No new pending POs found.');
      }
    } catch (err) {
      console.error("Failed to check pending POs:", err);
      alert("Failed to check pending POs");
    } finally {
      setChecking(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  };

  const confirmMaterialReceipt = async (poId, notificationId) => {
    await markAsRead(notificationId);
    navigate(`/purchase-orders/${poId}`);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-primary" data-testid="notifications-title">Notifications</h1>
          <p className="text-muted-foreground">Material receipt pending alerts and system notifications</p>
        </div>
        <Button 
          onClick={checkPendingPOs}
          disabled={checking}
          data-testid="check-pending-button"
          className="min-h-[44px]"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${checking ? "animate-spin" : ""}`} />
          {checking ? "Checking..." : "Check Pending POs"}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-primary" />
              <div>
                <div className="text-2xl font-bold">{notifications.length}</div>
                <div className="text-xs text-muted-foreground">Total Notifications</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={unreadCount > 0 ? "border-primary" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className={`w-5 h-5 ${unreadCount > 0 ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <div className="text-2xl font-bold">{unreadCount}</div>
                <div className="text-xs text-muted-foreground">Unread</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2 md:col-span-1">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div>
                <div className="text-xs text-muted-foreground">Auto-check for POs</div>
                <div className="text-sm font-medium">Older than 10 days</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center" data-testid="no-notifications">
            <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground mb-2">No notifications</p>
            <p className="text-sm text-muted-foreground">
              Click "Check Pending POs" to scan for purchase orders older than 10 days
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="notifications-list">
          {notifications.map((notification) => (
            <Card 
              key={notification.id}
              data-testid={`notification-${notification.id}`}
              className={notification.is_read ? "opacity-70" : "border-primary/30"}
            >
              <CardContent className="pt-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <Bell className={`w-5 h-5 ${notification.is_read ? "text-muted-foreground" : "text-primary"}`} />
                      <span className="font-mono text-sm font-semibold text-primary">
                        {notification.po_number || notification.wo_number || 'System'}
                      </span>
                      {!notification.is_read && (
                        <Badge variant="default" className="text-xs">New</Badge>
                      )}
                      {notification.notification_type && (
                        <Badge variant="outline" className="text-xs capitalize">
                          {notification.notification_type.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm mb-3">{notification.message}</p>
                    
                    {notification.pending_items && notification.pending_items.length > 0 && (
                      <div className="mb-3 p-3 bg-muted/30 rounded-lg">
                        <p className="text-xs font-bold uppercase text-muted-foreground mb-2 flex items-center gap-2">
                          <Package className="w-3 h-3" />
                          Pending Items:
                        </p>
                        <div className="space-y-1">
                          {notification.pending_items.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span>{item.product_name}</span>
                              <span className="font-mono">
                                <span className="text-green-600">{item.received}</span>
                                <span className="text-muted-foreground"> / </span>
                                <span>{item.ordered}</span>
                                <span className="text-orange-600 ml-2">({item.pending} pending)</span>
                              </span>
                            </div>
                          ))}
                          {notification.pending_items.length > 5 && (
                            <p className="text-xs text-muted-foreground italic">
                              + {notification.pending_items.length - 5} more items
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {notification.po_id && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/purchase-orders/${notification.po_id}`)}
                          data-testid={`view-po-${notification.id}`}
                          className="min-h-[44px]"
                        >
                          View PO
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => confirmMaterialReceipt(notification.po_id, notification.id)}
                          data-testid={`confirm-receipt-${notification.id}`}
                          className="min-h-[44px]"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          View & Confirm
                        </Button>
                      </>
                    )}
                    {notification.wo_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/production-tracking/${notification.wo_id}`)}
                        className="min-h-[44px]"
                      >
                        View WO
                      </Button>
                    )}
                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead(notification.id)}
                        data-testid={`mark-read-${notification.id}`}
                        className="min-h-[44px]"
                      >
                        Mark Read
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-base">Automatic Notification System</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• The system automatically checks for purchase orders older than 10 days</p>
          <p>• Notifications show which specific SKUs/items are pending receipt</p>
          <p>• Materials can be received partially and through multiple deliveries</p>
          <p>• Each item tracks: Ordered quantity, Received quantity, Pending quantity</p>
          <p>• Click "View & Confirm" to go to PO and confirm receipt for individual items</p>
        </CardContent>
      </Card>
    </div>
  );
}
